import { useState } from "react";
import { Holding, Cliente, Propiedad, Caso, CasoPropiedad, Contacto, Usuario } from "../../types";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { Badge, getTipoClienteBadgeVariant } from "../common/Badge";
import { HoldingForm } from "./HoldingForm";
import { HoldingDetail } from "./HoldingDetail";

interface HoldingsViewProps {
  holdings: Holding[];
  clientes: Cliente[];
  propiedades: Propiedad[];
  casos: Caso[];
  casoPropiedades: CasoPropiedad[];
  contactos: Contacto[];
  usuarios: Usuario[];
  onHoldingCreate: (holding: Omit<Holding, "id" | "fechaCreacion">) => void;
  onHoldingUpdate: (holding: Holding) => void;
  onHoldingDelete: (id: number) => void;
  onClienteAssignToHolding: (clienteId: number, holdingId: number) => void;
  onClienteRemoveFromHolding: (clienteId: number) => void;
  onClienteDelete: (id: number) => void;
  onPropiedadCreate: (data: Omit<Propiedad, "id" | "fechaCreacion">) => void;
  onPropiedadUpdate: (propiedad: Propiedad) => void;
  onPropiedadDelete: (id: number) => void;
  onCasoCreate: (data: Omit<Caso, "id" | "fechaCreacion" | "fechaActualizacion">) => void;
  onCasoPropiedadCreate: (data: Omit<CasoPropiedad, "id">) => void;
  usuarioActual: Usuario;
}

export function HoldingsView({
  holdings,
  clientes,
  propiedades,
  casos,
  casoPropiedades,
  contactos,
  usuarios,
  onHoldingCreate,
  onHoldingUpdate,
  onHoldingDelete,
  onClienteAssignToHolding,
  onClienteRemoveFromHolding,
  onClienteDelete,
  onPropiedadCreate,
  onPropiedadUpdate,
  onPropiedadDelete,
  onCasoCreate,
  onCasoPropiedadCreate,
  usuarioActual,
}: HoldingsViewProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState<string>("all");
  const [selectedHolding, setSelectedHolding] = useState<Holding | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingHolding, setEditingHolding] = useState<Holding | null>(null);
  const [holdingToDelete, setHoldingToDelete] = useState<Holding | null>(null);

  const filteredHoldings = holdings.filter((h) => {
    const matchesSearch =
      h.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      h.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = filterTipo === "all" || h.tipo === filterTipo;
    return matchesSearch && matchesTipo;
  });

  const getClientesCount = (holdingId: number) =>
    clientes.filter((c) => c.holdingId === holdingId).length;

  const getCasosCount = (holdingId: number) =>
    casos.filter((c) => c.holdingId === holdingId).length;

  const handleEdit = (holding: Holding) => {
    setEditingHolding(holding);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (data: Omit<Holding, "id" | "fechaCreacion">) => {
    if (editingHolding) {
      onHoldingUpdate({ ...editingHolding, ...data });
    } else {
      onHoldingCreate(data);
    }
    setIsFormOpen(false);
    setEditingHolding(null);
  };

  return (
    <div className="flex h-full">
      <div className="w-1/2 border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Holding</h2>
            <button
              onClick={() => { setEditingHolding(null); setIsFormOpen(true); }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Nuevo Holding
            </button>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              placeholder="Buscar holding..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos</option>
              <option value="cliente">Clientes</option>
              <option value="prospecto">Prospectos</option>
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-2">
            {filteredHoldings.map((holding) => (
              <div
                key={holding.id}
                onClick={() => setSelectedHolding(holding)}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedHolding?.id === holding.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-slate-800">{holding.nombre}</h3>
                      <Badge variant={getTipoClienteBadgeVariant(holding.tipo)}>
                        {holding.tipo}
                      </Badge>
                    </div>
                    {holding.descripcion && (
                      <p className="text-sm text-slate-500 mt-1">{holding.descripcion}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>{getClientesCount(holding.id)} Clientes</span>
                      <span>{getCasosCount(holding.id)} Casos</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(holding); }}
                      className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                      title="Editar"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setHoldingToDelete(holding); }}
                      className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                      title="Eliminar"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {filteredHoldings.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                {searchTerm || filterTipo !== "all" ? "No se encontraron holdings" : "No hay holdings registrados"}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="w-1/2 p-6 overflow-y-auto">
        {selectedHolding ? (
          <HoldingDetail
            holding={selectedHolding}
            clientes={clientes}
            propiedades={propiedades}
            casos={casos}
            casoPropiedades={casoPropiedades}
            contactos={contactos}
            usuarios={usuarios}
            onClienteAssignToHolding={onClienteAssignToHolding}
            onClienteRemoveFromHolding={onClienteRemoveFromHolding}
            onClienteDelete={onClienteDelete}
            onPropiedadCreate={onPropiedadCreate}
            onPropiedadUpdate={onPropiedadUpdate}
            onPropiedadDelete={onPropiedadDelete}
            onCasoCreate={onCasoCreate}
            onCasoPropiedadCreate={onCasoPropiedadCreate}
            usuarioActual={usuarioActual}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            Selecciona un holding para ver detalles
          </div>
        )}
      </div>

      <Modal
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingHolding(null); }}
        title={editingHolding ? "Editar Holding" : "Nuevo Holding"}
      >
        <HoldingForm
          holding={editingHolding}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsFormOpen(false); setEditingHolding(null); }}
        />
      </Modal>

      <ConfirmDialog
        isOpen={holdingToDelete !== null}
        onClose={() => setHoldingToDelete(null)}
        onConfirm={() => {
          if (holdingToDelete) {
            onHoldingDelete(holdingToDelete.id);
            if (selectedHolding?.id === holdingToDelete.id) setSelectedHolding(null);
          }
        }}
        title="Eliminar Holding"
        message={`Esta seguro que desea eliminar el holding "${holdingToDelete?.nombre}"?`}
        confirmText="Eliminar"
      />
    </div>
  );
}
